// Bottom-left toast queue for pickup / consume / damage / server messages.
// Borderless DayZ-style text: fade in 100ms, hold, fade 300ms, max 3 rows.
//
// TIERED, because one grey lane could not carry 31 call sites. Before this,
// "Sepsis — you need antibiotics NOW" was the same 12 px grey as "Reloading…",
// it held for the same 2.5 s, it had no mark of its own, and three repeats of
// "You are bleeding!" would push it out of a three-row lane entirely. Now:
//   • severity (info / warn / crit) picks the hold time, the mark and whether
//     the row gets a scrim — INFO ROWS LOOK EXACTLY AS THEY DID, which is 25
//     of the 31 call sites and every frame of the beauty-shot set;
//   • identical news dedupes into one row with a ×N counter instead of
//     evicting its neighbours;
//   • eviction is by severity, not by age, so pickup chatter can no longer
//     drop a warning off the screen.
// The severity, dedupe-key, hold and eviction rules are pure functions in
// ./hudmodel.mjs and are tested in tools/hudfeedback.mjs.
import { injectTheme } from './theme';
import { toastSeverity, toastKey, toastHold, evictIndex, SEV } from './hudmodel.mjs';

const MAX = 3;
const FADE = 300;

// 10px marks. Not emoji and not colour-only: a triangle for a warning, a
// filled disc with a stem for a critical one, so severity survives both a
// greyscale frame and a colour-blind player.
const MARK = {
  warn: '<svg class="tmk" viewBox="0 0 10 10" aria-hidden="true">'
    + '<path d="M5 .6 9.6 9H.4z" fill="none" stroke="currentColor" stroke-width="1.1"/>'
    + '<path d="M4.5 3.4h1v3h-1zM4.5 7h1v1h-1z" fill="currentColor"/></svg>',
  crit: '<svg class="tmk" viewBox="0 0 10 10" aria-hidden="true">'
    + '<path d="M5 .4 9.8 9.6H.2z" fill="currentColor"/>'
    + '<path d="M4.45 3.1h1.1v3.2H4.45z" fill="#120b0a"/>'
    + '<path d="M4.45 7h1.1v1.1H4.45z" fill="#120b0a"/></svg>',
};

export class Toasts {
  constructor() {
    injectTheme();
    const css = document.createElement('style');
    css.textContent = `
      /* 62px * --ui-scale, not a bare 62px: the weapon readout below this lane
         is ~37px tall AT SCALE 1 and ~56px at scale 1.5, so an unscaled offset
         (the old 64px) put the first toast through the ammo count on a 4K
         frame. Everything in the bottom-left column now scales together:
         weapon 0–37, noise meter 42–56, toasts 62 up. */
      #toasts { position:fixed; left:var(--hud-margin);
                bottom:calc(var(--hud-margin) + 62px * var(--ui-scale));
                z-index:11; display:flex; flex-direction:column-reverse; gap:4px;
                pointer-events:none; max-width:min(42vw, 420px); }
      #toasts .toast { font:400 12px/1.4 var(--ui-font); letter-spacing:var(--ui-track);
                       color:var(--hud-text); text-shadow:var(--hud-shadow);
                       opacity:0; transition:opacity .1s ease-out;
                       display:flex; align-items:baseline; gap:5px; }
      #toasts .toast.show { opacity:.92; }
      #toasts .toast.fade { opacity:0; transition:opacity .3s ease-in; }
      #toasts .tmk { width:10px; height:10px; flex:none; align-self:center; }
      /* A warning has to survive a sunlit background, and the text-shadow
         alone does not do it at 12px/400. The scrim is the contrast guarantee
         — the same argument as the weapon readout's shadow — and it is a
         DARKENING, so it cannot make the row the brightest thing in frame.
         Info rows get none of this and are pixel-identical to before. */
      #toasts .toast.warn, #toasts .toast.crit {
                       padding:1px 7px 1px 6px; background:rgba(8,8,8,.46);
                       border-left:2px solid currentColor; }
      #toasts .toast.warn { color:var(--hud-warn); }
      #toasts .toast.warn .tt { color:var(--hud-text); }
      #toasts .toast.crit { color:var(--hud-crit); background:rgba(10,4,3,.56); }
      /* the crit row's own text steps up one notch in weight, not in
         brightness — #f4f4f1 bottom-left is how round 5 lost the frame */
      #toasts .toast.crit .tt { color:var(--hud-text); font-weight:500; }
      #toasts .toast.crit .tmk { animation:tcrit 1.3s ease-in-out infinite; }
      @keyframes tcrit { 0%,100% { opacity:1; } 50% { opacity:.45; } }
      #toasts .tn { color:var(--hud-dim); font-variant-numeric:tabular-nums; }
    `;
    document.head.appendChild(css);
    this.root = document.createElement('div');
    this.root.id = 'toasts';
    document.body.appendChild(this.root);
    this.rows = [];   // oldest first; mirrors the DOM order of #toasts
  }

  // push(msg) — unchanged for every existing caller.
  // push(msg, 'crit') or push(msg, { sev, key }) for a caller that knows.
  // `key` groups rows that should dedupe even when the wording differs.
  push(msg, opts) {
    const o = typeof opts === 'string' ? { sev: opts } : (opts || {});
    const sev = toastSeverity(msg, o.sev);
    const key = toastKey(msg, o.key);

    // same news again: count it on the live row and restart its hold. This is
    // what stops a 0.6 blood/s wound nag from occupying the whole lane.
    const live = this.rows.find((r) => r.key === key && !r.fading);
    if (live) {
      live.n++;
      live.sev = SEV[sev] > SEV[live.sev] ? sev : live.sev;
      live.el.className = `toast show ${live.sev}`;
      this._fill(live, msg);
      this._arm(live);
      return;
    }

    if (this.rows.length >= MAX) {
      const sevs = this.rows.map((r) => r.sev);
      const i = evictIndex(sevs, sev);
      if (i < 0) {
        // Every live row outranks this one, so nothing may be displaced — but
        // DROPPING it was the wrong answer for the common case. A wolf fight
        // while feverish holds three warn rows ("A wolf bit you!", "You are
        // bleeding!", "You are shaking with fever") and each repeat re-arms its
        // 3.6 s hold, so the lane stays full — and hud.flashMove is the ONLY
        // answer F/G gives: open a door, take loot, butcher a corpse, refuse a
        // pickup for want of space, and the line was binned. A key the player
        // just pressed must never go unanswered, so the row gets a fourth slot.
        //
        // One exception, and it is the one the lane was designed around: while
        // anything CRITICAL is on screen the game is telling the player they
        // are dying, and a pickup line is noise there. Three crits still drop
        // it (tools/hudfeedback.mjs asserts exactly that).
        if (sevs.includes('crit') || this.rows.length > MAX) return;
      } else this._kill(this.rows[i], true);
    }

    const el = document.createElement('div');
    el.className = `toast ${sev}`;
    const row = { el, sev, key, n: 1, hold: null, fade: null, fading: false };
    this._fill(row, msg);
    this.root.appendChild(el);
    this.rows.push(row);
    requestAnimationFrame(() => el.classList.add('show'));
    this._arm(row);
  }

  _fill(row, msg) {
    const mark = MARK[row.sev] || '';
    const n = row.n > 1 ? `<span class="tn">×${row.n}</span>` : '';
    // textContent-safety: messages carry item names from the shared world, so
    // they are escaped rather than interpolated as markup
    const txt = document.createElement('span');
    txt.className = 'tt';
    txt.textContent = String(msg == null ? '' : msg);
    row.el.innerHTML = mark;
    row.el.appendChild(txt);
    if (n) row.el.insertAdjacentHTML('beforeend', n);
  }

  // (re)start the hold → fade → remove chain for one row
  _arm(row) {
    clearTimeout(row.hold);
    clearTimeout(row.fade);
    row.fading = false;
    row.el.classList.remove('fade');
    row.hold = setTimeout(() => {
      row.fading = true;
      row.el.classList.add('fade');
      row.fade = setTimeout(() => this._kill(row, true), FADE + 50);
    }, toastHold(row.sev));
  }

  _kill(row, remove) {
    clearTimeout(row.hold);
    clearTimeout(row.fade);
    const i = this.rows.indexOf(row);
    if (i >= 0) this.rows.splice(i, 1);
    if (remove) row.el.remove();
  }
}
